/* Systemet i tilbodet – sjølve systemet, ikkje eit bilete.
   Muligheitsbiletet: systemet nesten kant til kant, interaktivt – klikk deg rundt. Kapitla under ramma navigerer systemet.
   Same origin: ramma loggar inn i demo-kontoen «Ledelse» (sjå lib/authSync) – er lesaren alt innlogga, gjeld den økta.
   Feltdømet: telefonen frå presentasjonen (demodata, spelar av seg sjølv). Ingen data blir henta av tilbodssida sjølv. */
import { useEffect, useRef, useState } from "react";
import { PhoneMock, STEPS } from "@/presentasjon/feltSlide";
import { demoEntry } from "@/lib/authSync";
import { Inert, Stage, reducedMotion, useGeo } from "./Stage";

export const CHAPTERS = [
  { id: "oversikt", path: "/", cap: "Driftsoversikt", text: "Nøkkeltal, aktive riggar, kapasitet og den komande perioden samla i eitt bilete." },
  { id: "planlegging", path: "/planlegging", cap: "Planlegging", text: "Flåte, kontraktar, prosjekt og potensielle leveransar sett i samanheng, slik at kapasitetskonfliktar blir synlege tidleg." },
  { id: "prosjekt", path: "/prosjekter", cap: "Prosjekt", text: "Leveransen samla frå prosjektering til demontering – med fasar, modular, transport, oppgåver og avvik." },
  { id: "innkvartering", path: "/innkvartering", cap: "Innkvartering", text: "Rom, senger, belegg og måltid per rigg – tilpassa dei som faktisk skal planleggje og gjennomføre drifta." },
  { id: "assistent", path: "/assistent", cap: "Assistent", text: "Finn informasjon, svarar på spørsmål og lagar forslag basert på data frå systema. Ingenting blir endra utan at ein brukar tek stilling til det." },
];

const PHONE_W = 390 + 24;
const PHONE_H = 844 + 24;
const PHONE_PAD = 44;

/* monter først når ramma nærmar seg (sparar systemet for å starta tre gonger ved innlasting) */
const useNear = (ref, margin = "900px") => {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: `${margin} 0px` }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, margin]);
  return near;
};

const Loading = ({ text = "Lastar systemet …" }) => (
  <div className="sys-loading" aria-hidden="true">
    <span className="sys-loading-dot" /> {text}
  </div>
);

/* Prototypen blir vist innlogga i demo-kontoen «Ledelse» (lesetilgang på tvers). Er lesaren allereie innlogga, er det
   den økta som gjeld – same origin. */
export const DEMO_ROLE = "leder";
const entry = (path) => demoEntry(path, DEMO_ROLE);

/* ---------- systemet i ei rein ramme, skalert ---------- */
export const SystemFrame = ({ path = "/", interactive = false, height = 860, narrowHeight = 720, widths, testId = "offer-system-frame", className = "", frameRef, title = "Stord Innkvartering OS", onLoad }) => {
  const ref = useRef(null);
  const near = useNear(ref);
  const [loaded, setLoaded] = useState(false);
  const inner = useRef(null);
  const setRef = (el) => {
    inner.current = el;
    if (frameRef) frameRef.current = el;
  };
  return (
    <div ref={ref} className={`sys ${interactive ? "is-live" : "is-still"} ${loaded ? "is-loaded" : ""} ${className}`} data-testid={testId}>
      <Stage height={height} narrowHeight={narrowHeight} widths={widths} interactive={interactive} testId={`${testId}-stage`}>
        {() => (
          <>
            {!loaded && <Loading />}
            {near && (
              <iframe
                ref={setRef}
                src={entry(path)}
                title={title}
                className="sys-iframe"
                onLoad={() => {
                  setLoaded(true);
                  onLoad?.(inner.current);
                }}
                tabIndex={interactive ? 0 : -1}
                aria-hidden={interactive ? undefined : "true"}
                allow="clipboard-write; microphone; camera; geolocation"
                data-testid={`${testId}-iframe`}
              />
            )}
          </>
        )}
      </Stage>
    </div>
  );
};

/* ---------- visjonen: systemet nesten kant til kant, interaktivt, med kapittel under ---------- */
export const LiveSystem = () => {
  const frameRef = useRef(null);
  const wrapRef = useRef(null);
  const [, inView] = useGeo(wrapRef);
  const [current, setCurrent] = useState("oversikt");
  const [loaded, setLoaded] = useState(false);

  /* same origin: les kvar systemet er, så kapitla viser rett – utan å røra systemet. Hamnar ramma på innlogginga
     (t.d. etter «logg ut» inne i prototypen), går ho inn att i demoen. */
  useEffect(() => {
    if (!inView || !loaded) return undefined;
    const t = setInterval(() => {
      try {
        const loc = frameRef.current?.contentWindow?.location;
        const p = loc?.pathname || "";
        if (p === "/login") {
          if (!loc.search.includes("demo=")) loc.replace(entry("/"));
          return;
        }
        const hit = CHAPTERS.find((c) => (c.path === "/" ? p === "/" : p.startsWith(c.path)));
        setCurrent(hit ? hit.id : "");
      } catch {
        /* ignore */
      }
    }, 600);
    return () => clearInterval(t);
  }, [inView, loaded]);

  const go = (c) => {
    const win = frameRef.current?.contentWindow;
    try {
      /* replace: ingen ekstra steg i nettlesarhistorikken til sida */
      win.location.replace(c.path);
    } catch {
      if (frameRef.current) frameRef.current.src = c.path;
    }
    setCurrent(c.id);
  };

  return (
    <div ref={wrapRef} data-testid="offer-live">
      <div className="offer-live-wide">
        <SystemFrame
          path="/"
          interactive
          frameRef={frameRef}
          onLoad={() => setLoaded(true)}
          testId="offer-live-frame"
          className="offer-live-frame"
          widths={(w) => ({ fullWidth: Math.max(1360, w) })}
          height={(geo) => {
            const vh = typeof window !== "undefined" ? window.innerHeight : 900;
            if (geo.mode === "narrow") return 640;
            return Math.round(Math.min(1100, Math.max(620, (vh * 0.82) / geo.s)));
          }}
        />
      </div>
      <div className="offer-live-switch" role="group" aria-label="Kapittel i systemet" data-testid="offer-live-switch">
        {CHAPTERS.map((c) => (
          <button key={c.id} type="button" aria-pressed={current === c.id} onClick={() => go(c)} data-testid={`offer-live-tab-${c.id}`}>
            <b>{c.cap}</b>
            <span>{c.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ---------- telefonen frå presentasjonen (stillbilete/avspeling utan innlogging) – heroen på smale skjermar ---------- */
export const PhoneFigure = ({ width = 300, auto = true, index: fixed, className = "", testId = "offer-phone" }) => {
  const ref = useRef(null);
  const [, active] = useGeo(ref);
  const [w, setW] = useState(width + PHONE_PAD * 2);
  const [st, setSt] = useState({ index: fixed ?? 0, prev: null });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (!auto || fixed != null || !active || reducedMotion()) return undefined;
    const t = setTimeout(() => setSt((s) => ({ index: (s.index + 1) % STEPS.length, prev: s.index })), STEPS[st.index].dur);
    return () => clearTimeout(t);
  }, [auto, fixed, active, st]);
  const s = Math.max(0.3, (w - PHONE_PAD * 2) / PHONE_W);
  return (
    <div ref={ref} className={`offer-phone ${className}`} style={{ width: "100%", maxWidth: width + PHONE_PAD * 2, height: Math.round(PHONE_H * s) + 36 }} data-testid={testId} data-active={active ? "1" : "0"}>
      <div className="offer-phone-glow" aria-hidden="true" />
      <Inert className="offer-phone-inert">
        <div className="offer-phone-scale" style={{ transform: `scale(${s})`, width: PHONE_W, left: PHONE_PAD, top: 6 }}>
          <PhoneMock index={st.index} prevIndex={st.prev} />
        </div>
      </Inert>
    </div>
  );
};
